// ============================================
// VolumetricLightingPass — God rays / light scattering
// ============================================
//
// Renders volumetric light scattering (fog, god rays) by ray marching
// through the scene and sampling shadow maps.
//
// Runs at reduced resolution for performance (default 1/4).
// Output is additively blended into the final lit image.
//
// Extends DeferredPass for proper pipeline integration.
//
// Usage:
// ```ts
// const volumetricPass = new VolumetricLightingPass({ steps: 64 });
// deferredPassManager.addPass(volumetricPass);
// ```

import { DeferredPass, DeferredInjectionPoint, type DeferredPassResources } from '../pipeline/DeferredPass';
import type { IRenderContext } from '../core/IRenderContext';
import type { WebGPURenderContext } from '../core/WebGPURenderContext';
import type { TextureHandle, BufferHandle } from '../types';
import { NULL_HANDLE } from '../types';

// @ts-ignore — raw WGSL import
// Blit shader (Slang → WGSL, compiled into ShaderCache at engine startup).
import { getCompiledShader } from '../pipeline/ShaderCache';

// ============================================================================
// Configuration
// ============================================================================

export interface VolumetricLightingConfig {
  /** Resolution scale (0.25 = quarter res, 0.5 = half res). Default 0.25. */
  resolutionScale: number;
  /** Number of ray march steps. Higher = better quality, slower. Default 32. */
  steps: number;
  /** Maximum ray march distance in world units. Default 1000. */
  maxDistance: number;
}

const DEFAULT_CONFIG: VolumetricLightingConfig = {
  resolutionScale: 0.25,
  steps: 32,
  maxDistance: 1000,
};

// ============================================================================
// VolumetricLightingPass
// ============================================================================

export class VolumetricLightingPass extends DeferredPass {
  private config: VolumetricLightingConfig;
  private device: GPUDevice | null = null;

  // GPU resources
  private shaderModule: GPUShaderModule | null = null;
  private pipeline: GPURenderPipeline | null = null;
  private bindGroupLayout: GPUBindGroupLayout | null = null;
  private sampler: GPUSampler | null = null;
  private volConfigBuffer: GPUBuffer | null = null; // steps + maxDistance (was pipeline overrides)

  // Offscreen render target (reduced resolution)
  private volumetricTexture: GPUTexture | null = null;
  private volumetricView: GPUTextureView | null = null;
  private _volumetricTextureHandle: TextureHandle = NULL_HANDLE;

  // Bind group cache
  private bindGroup: GPUBindGroup | null = null;
  private lastLightBuf: GPUBuffer | null = null;
  private lastUniformBuf: GPUBuffer | null = null;
  private lastShadowBuf: GPUBuffer | null = null;

  // Dummy shadow buffer for when no shadows exist
  private _dummyShadowBuffer: GPUBuffer | null = null;

  // Composite pipeline for additive blit to output
  private compositePipelineLDR: GPURenderPipeline | null = null;
  private compositePipelineHDR: GPURenderPipeline | null = null;
  private compositeBindGroupLayout: GPUBindGroupLayout | null = null;
  private compositeSampler: GPUSampler | null = null;
  private compositeUniformBuffer: GPUBuffer | null = null;
  private compositeBindGroup: GPUBindGroup | null = null;

  private _width = 0;
  private _height = 0;
  private _scaledWidth = 0;
  private _scaledHeight = 0;

  constructor(config: Partial<VolumetricLightingConfig> = {}) {
    super('VolumetricLighting', DeferredInjectionPoint.AfterShadows);
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // ========================================================================
  // DeferredPass Implementation
  // ========================================================================

  protected override onInitialize(ctx: IRenderContext): void {
    const device = ctx.getDevice?.();
    if (!device) {
      console.warn('[VolumetricLightingPass] Requires WebGPU device');
      return;
    }
    this.device = device;
    this.initPipeline(ctx);
  }

  protected override onExecute(resources: DeferredPassResources): void {
    if (!this.device || !this.pipeline) return;

    const { ctx, width, height, lightUniformBuffer, lightBuffer, shadowBuffer } = resources;

    // Resize if needed
    if (width !== this._width || height !== this._height) {
      this.resizeInternal(width, height);
    }

    if (!this.volumetricView) return;

    // Resolve GPU resources
    const maps = (ctx as WebGPURenderContext).getResourceMaps();

    const lightBuf = maps.buffers.get(lightBuffer);
    const uniformBuf = maps.buffers.get(lightUniformBuffer);

    if (!lightBuf || !uniformBuf) return;

    // Resolve shadow buffer
    let shadowBuf: GPUBuffer;
    if (shadowBuffer != null) {
      const resolved = maps.buffers.get(shadowBuffer);
      shadowBuf = resolved ? resolved.gpuBuffer : this.getDummyShadowBuffer();
    } else {
      shadowBuf = this.getDummyShadowBuffer();
    }

    // Rebuild bind group if resources changed
    const needsRebuild = !this.bindGroup
      || this.lastLightBuf !== lightBuf.gpuBuffer
      || this.lastUniformBuf !== uniformBuf.gpuBuffer
      || this.lastShadowBuf !== shadowBuf;

    if (needsRebuild) {
      this.bindGroup = this.device.createBindGroup({
        layout: this.bindGroupLayout!,
        entries: [
          { binding: 0, resource: { buffer: uniformBuf.gpuBuffer } },
          { binding: 1, resource: { buffer: lightBuf.gpuBuffer } },
          { binding: 2, resource: { buffer: shadowBuf } },
          { binding: 3, resource: { buffer: this.volConfigBuffer! } },
        ],
      });
      this.lastLightBuf = lightBuf.gpuBuffer;
      this.lastUniformBuf = uniformBuf.gpuBuffer;
      this.lastShadowBuf = shadowBuf;
    }

    // Render volumetric pass
    const encoder = this.device.createCommandEncoder({ label: 'volumetric-lighting' });

    const pass = encoder.beginRenderPass({
      colorAttachments: [{
        view: this.volumetricView,
        loadOp: 'clear',
        clearValue: { r: 0, g: 0, b: 0, a: 0 },
        storeOp: 'store',
      }],
      label: 'volumetric-pass',
    });

    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup!);
    pass.draw(3); // Fullscreen triangle
    pass.end();

    this.device.queue.submit([encoder.finish()]);
  }

  protected override onDispose(_ctx: IRenderContext): void {
    this.volumetricTexture?.destroy();
    this._dummyShadowBuffer?.destroy();
    this.volConfigBuffer?.destroy();
    this.volConfigBuffer = null;
    this.pipeline = null;
    this.shaderModule = null;
    this.bindGroupLayout = null;
    this.sampler = null;
    this.volumetricTexture = null;
    this.volumetricView = null;
    this.bindGroup = null;
    this.device = null;
  }

  protected override onResize(width: number, height: number): void {
    this.resizeInternal(width, height);
  }

  // ========================================================================
  // Public API
  // ========================================================================

  /** Whether the pass is ready to render. */
  get isReady(): boolean {
    return this.pipeline !== null;
  }

  /** Current configuration. */
  get currentConfig(): Readonly<VolumetricLightingConfig> {
    return this.config;
  }

  /** The volumetric fog texture (reduced resolution). */
  get volumetricTextureHandle(): TextureHandle {
    return this._volumetricTextureHandle;
  }

  /** Update configuration. steps/maxDistance are now live uniforms — no pipeline rebuild needed. */
  setConfig(config: Partial<VolumetricLightingConfig>): void {
    this.config = { ...this.config, ...config };
    this.writeVolConfig();
  }

  /** Upload the volumetric config uniform (steps + maxDistance). */
  private writeVolConfig(): void {
    if (!this.device || !this.volConfigBuffer) return;
    const buf = new ArrayBuffer(16); // steps (u32), _pad0, maxDistance (f32), _pad1
    new Uint32Array(buf, 0, 1)[0] = this.config.steps;
    new Float32Array(buf, 8, 1)[0] = this.config.maxDistance;
    this.device.queue.writeBuffer(this.volConfigBuffer, 0, buf);
  }

  /** Get the raw GPU texture view for compositing. */
  getVolumetricView(): GPUTextureView | null {
    return this.volumetricView;
  }

  /** Get scaled dimensions. */
  getScaledDimensions(): { width: number; height: number } {
    return { width: this._scaledWidth, height: this._scaledHeight };
  }

  /**
   * Direct render method for manual integration (bypasses DeferredPassManager).
   * 
   * @param ctx - Render context
   * @param lightUniformBuffer - Light uniform buffer handle
   * @param lightBuffer - Light storage buffer handle
   * @param shadowBuffer - Shadow map buffer handle (or null)
   * @param _depthTexture - Unused (kept for API compatibility)
   * @param width - Viewport width
   * @param height - Viewport height
   */
  render(
    ctx: IRenderContext,
    lightUniformBuffer: BufferHandle,
    lightBuffer: BufferHandle,
    shadowBuffer: BufferHandle | null,
    _depthTexture: TextureHandle,
    width: number,
    height: number,
  ): void {
    // Initialize on first render if not yet done
    if (!this.isInitialized) {
      this.initialize(ctx);
    }

    if (!this.device || !this.pipeline) return;

    // Resize if needed
    if (width !== this._width || height !== this._height) {
      this.resizeInternal(width, height);
    }

    if (!this.volumetricView) return;

    // Resolve GPU resources
    const maps = (ctx as WebGPURenderContext).getResourceMaps();

    const lightBuf = maps.buffers.get(lightBuffer);
    const uniformBuf = maps.buffers.get(lightUniformBuffer);

    if (!lightBuf || !uniformBuf) return;

    // Resolve shadow buffer
    let shadowBuf: GPUBuffer;
    if (shadowBuffer != null) {
      const resolved = maps.buffers.get(shadowBuffer);
      shadowBuf = resolved ? resolved.gpuBuffer : this.getDummyShadowBuffer();
    } else {
      shadowBuf = this.getDummyShadowBuffer();
    }

    // Rebuild bind group if resources changed
    const needsRebuild = !this.bindGroup
      || this.lastLightBuf !== lightBuf.gpuBuffer
      || this.lastUniformBuf !== uniformBuf.gpuBuffer
      || this.lastShadowBuf !== shadowBuf;

    if (needsRebuild) {
      this.bindGroup = this.device.createBindGroup({
        layout: this.bindGroupLayout!,
        entries: [
          { binding: 0, resource: { buffer: uniformBuf.gpuBuffer } },
          { binding: 1, resource: { buffer: lightBuf.gpuBuffer } },
          { binding: 2, resource: { buffer: shadowBuf } },
          { binding: 3, resource: { buffer: this.volConfigBuffer! } },
        ],
      });
      this.lastLightBuf = lightBuf.gpuBuffer;
      this.lastUniformBuf = uniformBuf.gpuBuffer;
      this.lastShadowBuf = shadowBuf;
    }

    // Render volumetric pass
    const encoder = this.device.createCommandEncoder({ label: 'volumetric-lighting' });

    const pass = encoder.beginRenderPass({
      colorAttachments: [{
        view: this.volumetricView,
        loadOp: 'clear',
        clearValue: { r: 0, g: 0, b: 0, a: 0 },
        storeOp: 'store',
      }],
      label: 'volumetric-pass',
    });

    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup!);
    pass.draw(3); // Fullscreen triangle
    pass.end();

    this.device.queue.submit([encoder.finish()]);
  }

  /**
   * Composite volumetric lighting to the specified output target with additive blending.
   * Call this after `render()` and after the main light accumulation pass.
   * 
   * @param outputView - GPU texture view to composite onto (typically the lit scene)
   * @param isHdrTarget - true when compositing onto an rgba16float scene target (HDR pipeline path)
   */
  compositeToTarget(outputView: GPUTextureView, isHdrTarget = false): void {
    const compositePipeline = isHdrTarget ? this.compositePipelineHDR : this.compositePipelineLDR;
    if (!this.device || !compositePipeline || !this.volumetricView) return;

    // Rebuild composite bind group if volumetric view changed
    if (!this.compositeBindGroup) {
      this.compositeBindGroup = this.device.createBindGroup({
        layout: this.compositeBindGroupLayout!,
        entries: [
          { binding: 0, resource: { buffer: this.compositeUniformBuffer! } },
          { binding: 1, resource: this.compositeSampler! },
          { binding: 2, resource: this.volumetricView },
        ],
      });
    }

    // Encode composite pass
    const encoder = this.device.createCommandEncoder({ label: 'volumetric-composite' });

    const pass = encoder.beginRenderPass({
      colorAttachments: [{
        view: outputView,
        loadOp: 'load', // Preserve existing lit content
        storeOp: 'store',
      }],
      label: 'volumetric-composite-pass',
    });

    pass.setPipeline(compositePipeline);
    pass.setBindGroup(0, this.compositeBindGroup);
    pass.draw(3); // Fullscreen triangle
    pass.end();

    this.device.queue.submit([encoder.finish()]);
  }

  // ========================================================================
  // Internals
  // ========================================================================

  private initPipeline(ctx: IRenderContext): void {
    if (!this.device) return;

    // Precompiled Slang → WGSL from the startup cache
    this.shaderModule = this.device.createShaderModule({
      code: getCompiledShader('lighting/volumetric-lighting'),
      label: 'volumetric-lighting-shader',
    });

    // Bind group layout (no depth texture for 2D volumetrics)
    this.bindGroupLayout = this.device.createBindGroupLayout({
      label: 'volumetric-bgl',
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'read-only-storage' } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'read-only-storage' } },
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } }, // VolumetricConfig
      ],
    });

    const pipelineLayout = this.device.createPipelineLayout({
      bindGroupLayouts: [this.bindGroupLayout],
      label: 'volumetric-layout',
    });

    // Additive blend for compositing
    const blendState: GPUBlendState = {
      color: { srcFactor: 'one', dstFactor: 'one', operation: 'add' },
      alpha: { srcFactor: 'one', dstFactor: 'one', operation: 'add' },
    };

    // Create pipeline with override constants
    this.pipeline = this.device.createRenderPipeline({
      layout: pipelineLayout,
      vertex: { module: this.shaderModule, entryPoint: 'vs_main' },
      fragment: {
        module: this.shaderModule,
        entryPoint: 'fs_main',
        targets: [{ format: 'rgba16float', blend: blendState }],
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      label: 'volumetric-pipeline',
    });

    // Volumetric config uniform (steps + maxDistance; was pipeline overrides).
    this.volConfigBuffer = this.device.createBuffer({
      size: 16,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      label: 'volumetric-config',
    });
    this.writeVolConfig();

    // ── Composite pipeline for additive blit to output ──
    this.initCompositePipeline(ctx.getSwapChainFormat() as GPUTextureFormat);
  }

  /** Initialize the composite pipelines (LDR backbuffer + HDR scene target) for additive blitting. */
  private initCompositePipeline(swapChainFormat: GPUTextureFormat): void {
    if (!this.device) return;

    const compositeShaderModule = this.device.createShaderModule({
      code: getCompiledShader('blit'),
      label: 'volumetric-composite-shader',
    });

    // Bind group layout: uniform buffer + sampler + texture
    this.compositeBindGroupLayout = this.device.createBindGroupLayout({
      label: 'volumetric-composite-bgl',
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
      ],
    });

    const compositePipelineLayout = this.device.createPipelineLayout({
      bindGroupLayouts: [this.compositeBindGroupLayout],
      label: 'volumetric-composite-layout',
    });

    // Sampler for upscale/blit
    this.compositeSampler = this.device.createSampler({
      minFilter: 'linear',
      magFilter: 'linear',
      label: 'volumetric-composite-sampler',
    });

    // Uniform buffer for opacity (always 1.0 for volumetrics)
    this.compositeUniformBuffer = this.device.createBuffer({
      size: 16, // BlitUniforms: f32 opacity + 3x f32 padding
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      label: 'volumetric-composite-uniforms',
    });
    this.device.queue.writeBuffer(this.compositeUniformBuffer, 0, new Float32Array([1.0, 0, 0, 0]));

    // Additive blend for compositing volumetrics
    const compositeBlend: GPUBlendState = {
      color: { srcFactor: 'one', dstFactor: 'one', operation: 'add' },
      alpha: { srcFactor: 'one', dstFactor: 'one', operation: 'add' },
    };

    // LDR pipeline targets the real swap-chain format (was hardcoded 'bgra8unorm'); HDR targets the
    // rgba16float scene buffer so light shafts stay unclamped through bloom/tonemap.
    this.compositePipelineLDR = this.device.createRenderPipeline({
      layout: compositePipelineLayout,
      vertex: { module: compositeShaderModule, entryPoint: 'vs_main' },
      fragment: {
        module: compositeShaderModule,
        entryPoint: 'fs_main',
        targets: [{ format: swapChainFormat, blend: compositeBlend }],
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      label: 'volumetric-composite-pipeline-ldr',
    });

    this.compositePipelineHDR = this.device.createRenderPipeline({
      layout: compositePipelineLayout,
      vertex: { module: compositeShaderModule, entryPoint: 'vs_main' },
      fragment: {
        module: compositeShaderModule,
        entryPoint: 'fs_main',
        targets: [{ format: 'rgba16float', blend: compositeBlend }],
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      label: 'volumetric-composite-pipeline-hdr',
    });
  }

  private resizeInternal(width: number, height: number): void {
    if (!this.device) return;

    this._width = width;
    this._height = height;
    this._scaledWidth = Math.max(1, Math.floor(width * this.config.resolutionScale));
    this._scaledHeight = Math.max(1, Math.floor(height * this.config.resolutionScale));

    // Destroy old texture
    this.volumetricTexture?.destroy();

    // Create new texture at reduced resolution
    this.volumetricTexture = this.device.createTexture({
      size: [this._scaledWidth, this._scaledHeight],
      format: 'rgba16float', // HDR for bright light shafts
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
      label: 'volumetric-texture',
    });
    this.volumetricView = this.volumetricTexture.createView();

    // Invalidate bind groups
    this.bindGroup = null;
    this.compositeBindGroup = null;
  }

  private getDummyShadowBuffer(): GPUBuffer {
    if (!this._dummyShadowBuffer && this.device) {
      this._dummyShadowBuffer = this.device.createBuffer({
        size: 4,
        usage: GPUBufferUsage.STORAGE,
        label: 'dummy-shadow-volumetric',
      });
    }
    return this._dummyShadowBuffer!;
  }
}
